import { UserAction } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import clsx from "clsx";
import { StarIcon } from "lucide-react";
import { useMemo } from "react";

import CertificationPassedCard from "./CertificationPassedCard";
import CertificationSelect from "./CertificationSelect";
import CertificationWorkshopPopover from "./CertificationWorkshopPopover";
import {
	useCertification,
	useCertifications,
	usePassedCertifications,
	useSelectedCertification,
	useUserAction,
} from "~/hooks";
import useTrackCTA from "~/hooks/useTrackCTA";

const CertificationProgress = () => {
	const { certification } = useCertification();
	const { certifications } = useCertifications();
	const { userId, isLoaded } = useAuth();

	const progress = useMemo(
		() =>
			certification.earnedTotalCredits &&
			(certification.earnedTotalCredits / certification.totalCredits) * 100,
		[certification.earnedTotalCredits, certification.totalCredits],
	);

	const { passedCertifications } = usePassedCertifications();
	const { selectedCertification, handleSelectCertification } =
		useSelectedCertification();

	const isSelected = selectedCertification?.slug === certification.slug;

	const passedCertification = useMemo(
		() =>
			passedCertifications?.find(
				(passedCertification) =>
					passedCertification.certificationId === certification._id,
			),
		[passedCertifications, certification._id],
	);

	const { setUserAction } = useUserAction();

	const trackCTA = useTrackCTA();

	const isCandidate =
		passedCertification && (passedCertification as any).isCandidate;

	if (certification.isStudyPlan) {
		return (
			<div className={clsx(`relative flex w-full flex-col items-center gap-5`)}>
				<div className="flex w-full flex-col gap-4 phablet:gap-5">
					<div className="flex items-center justify-between gap-5">
						<CertificationSelect certifications={certifications} />
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={clsx(`relative flex w-full flex-col items-center gap-5`)}>
			<div className="flex w-full flex-col gap-4 phablet:gap-5">
				<div className="flex items-center justify-between gap-5">
					<div className="flex items-center gap-2">
						<button
							disabled={!isLoaded}
							className={clsx(
								"-ml-0.5 rounded-full p-1.5 hover:brightness-90 dark:hover:brightness-110",
								isSelected
									? "bg-amber-100 dark:bg-amber-300/20"
									: "bg-slate-100 dark:bg-slate-700",
							)}
							onClick={(e) => {
								e.preventDefault();

								if (!userId) {
									trackCTA(UserAction.SIGN_UP, "Certification Select");

									setUserAction(UserAction.SIGN_UP);
								} else {
									isSelected
										? handleSelectCertification(null)
										: handleSelectCertification(certification);
								}
							}}
						>
							<StarIcon
								size={18}
								className={clsx(
									isSelected
										? "fill-amber-500 text-amber-500"
										: "fill-slate-300 text-slate-300 dark:fill-slate-400 dark:text-slate-400",
								)}
							/>
						</button>

						<CertificationSelect certifications={certifications} />
					</div>

					<div
						className={clsx(
							"relative shrink-0 overflow-hidden rounded-xl border px-3 py-1 font-medium dark:bg-slate-700",
						)}
						id="certification-progress"
					>
						<div
							className={clsx(
								"absolute top-0 left-0 h-full w-full bg-emerald-200 shadow-xl dark:bg-emerald-500/50",
							)}
							style={{
								width: certification.earnedTotalCredits ? `${progress}%` : "0",
							}}
						>
							{" "}
						</div>

						<div className="relative dark:text-slate-50">
							<span className="mr-1">
								{certification.earnedTotalCredits}{" "}
								{certification.earnedTotalCredits !== undefined && " / "}
								{certification.totalCredits}
							</span>
							Credits
						</div>
					</div>
				</div>

				{(!passedCertification || isCandidate) && (
					<CertificationWorkshopPopover />
				)}

				<CertificationPassedCard certification={certification} />
			</div>
		</div>
	);
};

export default CertificationProgress;
